import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import gorillian from "../../images/gorillian-white.png";

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uTexture;
  uniform float uTime;
  uniform float uIntensity;
  uniform float uProgress;
  uniform float uLogoScale;
  uniform vec2 uResolution;
  uniform vec2 uImageSize;
  uniform vec2 uMouse;
  uniform float uFit;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 5; i++) {
      v += a * noise(p);
      p = p * 2.07 + vec2(1.7, 9.2);
      a *= 0.5;
    }
    return v;
  }

  vec2 fitUv(vec2 uv) {
    float canvasAspect = uResolution.x / max(uResolution.y, 1.0);
    float imageAspect = uImageSize.x / max(uImageSize.y, 1.0);
    vec2 centered = uv - 0.5;
    if (uFit > 0.5) {
      if (canvasAspect > imageAspect) {
        centered.y *= imageAspect / canvasAspect;
      } else {
        centered.x *= canvasAspect / imageAspect;
      }
      return centered + 0.5;
    }
    if (canvasAspect > imageAspect) {
      float sx = imageAspect / canvasAspect;
      centered.x /= sx * uLogoScale;
      centered.y /= uLogoScale;
    } else {
      float sy = canvasAspect / imageAspect;
      centered.x /= uLogoScale;
      centered.y /= sy * uLogoScale;
    }
    return centered + 0.5;
  }

  vec4 sampleLogo(vec2 uv) {
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
      return vec4(0.0);
    }
    return texture2D(uTexture, uv);
  }

  void main() {
    vec2 uv = vUv;
    vec2 logoUv = fitUv(uv);
    vec2 toMouse = uv - uMouse;
    float mouseDist = length(toMouse);
    float mouseWell = exp(-mouseDist * 8.0) * (0.35 + uIntensity);

    float t = uTime;
    vec2 warpField = vec2(
      fbm(logoUv * 2.8 + vec2(t * 0.11, -t * 0.07)),
      fbm(logoUv * 2.8 + vec2(-t * 0.08, t * 0.13) + 19.2)
    );
    vec2 warp = (warpField - 0.5) * mix(0.004, 0.012, uIntensity);

    float bandId = floor((uv.y + t * 0.18) * 22.0);
    float glitchGate = step(0.965, hash(vec2(bandId, floor(t * 3.0))));
    warp.x += (hash(vec2(t, bandId)) - 0.5) * glitchGate * 0.026 * uIntensity;

    float mouseBand = exp(-abs(uv.y - uMouse.y) * 16.0);
    float tracking = hash(vec2(floor(uv.y * 220.0), floor(t * 14.0))) - 0.5;
    warp.x += tracking * mix(0.001, 0.004, mouseBand) * (0.45 + uIntensity);

    float reveal = 1.0 - uProgress;
    float sliceId = floor(logoUv.y * 52.0);
    float sliceHash = hash(vec2(sliceId, 11.3));
    float sliceOn = step(sliceHash * 0.88, uProgress + 0.03);
    float sliceJitter = hash(vec2(sliceId, floor(t * 18.0 + uProgress * 12.0))) - 0.5;
    warp.x += sliceJitter * reveal * 0.11;

    vec2 sampleUv = logoUv + warp;

    vec2 fromCenter = uv - 0.5;
    float ca = mix(0.0035, 0.009, uIntensity) + mouseWell * 0.008 + reveal * 0.02;
    vec2 caDir = vec2(1.0, 0.0);

    vec4 base = sampleLogo(sampleUv);
    float r = sampleLogo(sampleUv + caDir * ca).r;
    float g = base.g;
    float b = sampleLogo(sampleUv - caDir * ca).b;

    float glow = 0.0;
    vec2 texel = vec2(1.6 / max(uResolution.x, 1.0), 1.6 / max(uResolution.y, 1.0));
    glow += sampleLogo(sampleUv + vec2(texel.x, 0.0)).a;
    glow += sampleLogo(sampleUv - vec2(texel.x, 0.0)).a;
    glow += sampleLogo(sampleUv + vec2(0.0, texel.y)).a;
    glow += sampleLogo(sampleUv - vec2(0.0, texel.y)).a;
    glow += sampleLogo(sampleUv + texel).a;
    glow += sampleLogo(sampleUv - texel).a;
    glow = max(glow / 6.0 - base.a, 0.0);

    vec3 tint = mix(vec3(1.0), vec3(1.0, 0.72, 0.48), mouseWell * 0.4);
    vec3 color = vec3(r, g, b) * tint;
    color += vec3(1.0, 0.62, 0.32) * glow * mix(0.22, 0.55, uIntensity);
    color += vec3(1.0, 0.25, 0.48) * glitchGate * base.a * 0.18 * uIntensity;

    float grain = (hash(uv * uResolution + t * 90.0) - 0.5) * mix(0.012, 0.005, uIntensity);
    color += grain;

    float scan = 1.0 - 0.18 * (0.5 + 0.5 * sin(uv.y * uResolution.y * 3.14159));
    float flicker = 1.0 - 0.045 * hash(vec2(floor(t * 18.0), 3.1));
    color *= scan * flicker;

    float row = floor(uv.y * 40.0);
    float col = floor(uv.x * 7.0);
    float blockOn = step(hash(vec2(row, col + 4.2)) * 0.9, uProgress);
    float locked = smoothstep(0.78, 1.0, uProgress);
    float dissolve = mix(sliceOn * blockOn, 1.0, locked);

    float vignette = smoothstep(1.15, 0.28, length(fromCenter));
    float edgeFade = uFit > 0.5 ? 1.0 : mix(0.55, 1.0, vignette);
    float alpha = clamp(max(base.a, glow * 0.85) * dissolve * edgeFade, 0.0, 1.0);

    gl_FragColor = vec4(color, alpha);
  }
`;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function GorillianCanvas({
  src = gorillian,
  fit = "contain",
  intensity = 1,
  progress = 1,
  logoScale = 0.72,
  hoverTint = true,
  className = "",
  alt = "gorillian",
}) {
  const containerRef = useRef(null);
  const targetsRef = useRef({ intensity, progress, logoScale });
  const [failed, setFailed] = useState(false);

  targetsRef.current = { intensity, progress, logoScale };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || prefersReducedMotion()) {
      if (prefersReducedMotion()) setFailed(true);
      return;
    }

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
      });
    } catch {
      setFailed(true);
      return;
    }

    const getSize = () => ({
      w: container.clientWidth || window.innerWidth,
      h: container.clientHeight || window.innerHeight,
    });

    const { w, h } = getSize();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geometry = new THREE.PlaneGeometry(2, 2);

    const uniforms = {
      uTexture: { value: null },
      uTime: { value: 0 },
      uIntensity: { value: intensity },
      uProgress: { value: progress },
      uLogoScale: { value: logoScale },
      uResolution: { value: new THREE.Vector2(w, h) },
      uImageSize: { value: new THREE.Vector2(3300, 2400) },
      uMouse: { value: new THREE.Vector2(0.5, 0.5) },
      uFit: { value: fit === "cover" ? 1 : 0 },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const loader = new THREE.TextureLoader();
    loader.load(
      src,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        uniforms.uTexture.value = texture;
        uniforms.uImageSize.value.set(
          texture.image.width,
          texture.image.height,
        );
      },
      undefined,
      () => setFailed(true),
    );

    const canHover =
      hoverTint &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const pointer = canHover
      ? { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 }
      : { x: -1, y: -1, tx: -1, ty: -1 };
    const current = {
      intensity,
      progress,
      logoScale,
    };

    const onPointer = (event) => {
      const rect = container.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      pointer.tx = (clientX - rect.left) / Math.max(rect.width, 1);
      pointer.ty = 1 - (clientY - rect.top) / Math.max(rect.height, 1);
    };
    const resetPointer = () => {
      pointer.tx = 0.5;
      pointer.ty = 0.5;
    };

    if (canHover) {
      window.addEventListener("pointermove", onPointer, { passive: true });
      window.addEventListener("pointerleave", resetPointer);
    }

    const clock = new THREE.Clock();
    let frameId;
    let running = true;

    const animate = () => {
      if (!running) return;
      frameId = requestAnimationFrame(animate);
      const dt = Math.min(clock.getDelta(), 0.05);
      const targets = targetsRef.current;

      current.intensity += (targets.intensity - current.intensity) * 0.045;
      current.progress += (targets.progress - current.progress) * 0.06;
      current.logoScale += (targets.logoScale - current.logoScale) * 0.045;
      pointer.x += (pointer.tx - pointer.x) * 0.08;
      pointer.y += (pointer.ty - pointer.y) * 0.08;

      uniforms.uTime.value += dt;
      uniforms.uIntensity.value = current.intensity;
      uniforms.uProgress.value = current.progress;
      uniforms.uLogoScale.value = current.logoScale;
      uniforms.uMouse.value.set(pointer.x, pointer.y);
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const { w: nextW, h: nextH } = getSize();
      if (nextW < 2 || nextH < 2) return;
      renderer.setSize(nextW, nextH);
      uniforms.uResolution.value.set(nextW, nextH);
    };
    window.addEventListener("resize", onResize);
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(frameId);
      } else if (!running) {
        running = true;
        clock.getDelta();
        animate();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Initial uniform values come from first render; later updates lerp via targetsRef.
    return () => {
      running = false;
      cancelAnimationFrame(frameId);
      if (canHover) {
        window.removeEventListener("pointermove", onPointer);
        window.removeEventListener("pointerleave", resetPointer);
      }
      window.removeEventListener("resize", onResize);
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      geometry.dispose();
      material.dispose();
      if (uniforms.uTexture.value) uniforms.uTexture.value.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
    // Props are read from targetsRef so the WebGL context is not rebuilt on each tween.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <div className={`gorillian-canvas fallback ${className}`.trim()}>
        <img src={src} alt={alt} className="gorillian-fallback" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`gorillian-canvas ${className}`.trim()}
      aria-hidden="true"
    />
  );
}
